import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { onAuthStateChange, loginWithGoogle, logoutUser, LoginPage } from './auth';
import { getOptimizedProfilePhoto } from './services/userService';
import { initializeSystemSettings } from './services/systemSettingsService';
import { checkIsCurrentUserAdmin, refreshAdminStatus } from './services/adminService';
import initializeCardTemplatesIfNeeded from './services/cardTemplatesInitializer';
import { initializeConsentBasedServices } from './services/cookieService';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import Pricing from './pages/pricing-page';
import Career from './pages/career-page';
import { HomePage, Dashboard, AccountSettings } from './pages';
import AnalysisProgress from './components/AnalysisProgress';
import PrivacyPolicy from './pages/privacy-policy';
import AboutPage from './pages/about-page';
import ContactPage from './pages/contact-page';
import TermsPage from './pages/terms-page';
import CookiesPage from './pages/cookies-page';
import DocumentationPage from './pages/documentation-page';
import SupportPage from './pages/support-page';
import NewProject from './pages/new-project-page/NewProject';
import AppAnalysisDetails from './pages/app-analysis-details-page/AppAnalysisDetails';
import SystemSettings from './pages/system-settings-page/SystemSettings';
import AdminManagementPage from './pages/admin-management-page/AdminManagement';
import NotFound from './pages/not-found-page';
import AppDetailsPage from './pages/app-details/AppDetailsPage';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from './config/firebase';
import DeployStatusUpdatePage from './pages/system-settings-page/admin-user-details/DeployStatusUpdatePage';
import AppStatusPage from './pages/app-status/AppStatusPage';
import AdminUserDetailsPage from './pages/system-settings-page/admin-user-details/AdminUserDetailsPage';
import PageTitle from './pages/system-settings-page/PageTitle';

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [redirectPath, setRedirectPath] = useState('');
    const [systemInitialized, setSystemInitialized] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminChecked, setAdminChecked] = useState(false);

    // Initialize system settings
    useEffect(() => {
        const initSystem = async () => {
            try {
                await initializeSystemSettings();
                
                // Initialize card templates if needed
                await initializeCardTemplatesIfNeeded();
                
                // Initialize cookie consent based services
                initializeConsentBasedServices();
                
                setSystemInitialized(true);
                console.log('System settings, templates, and cookie services initialized successfully');
            } catch (error) {
                console.error('Error initializing system settings:', error);
                // Continue with app initialization even if system settings fail
                setSystemInitialized(true);
            }
        };

        initSystem();
    }, []);

    // Check authentication status
    useEffect(() => {
        const unsubscribe = onAuthStateChange((user) => {
            setIsLoggedIn(!!user);
            if (user) {
                const firstName = user.displayName ? user.displayName.split(' ')[0] : '';
                
                // Get optimized profile photo using the dedicated function
                const photoURL = getOptimizedProfilePhoto(user);
                
                console.log('User logged in with data:', {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: photoURL
                });
                
                setUser({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    firstName: firstName,
                    photoURL: photoURL
                });
                setError('');
                
                // Update or create user document in Firestore
                const updateUserDocument = async () => {
                    try {
                        const userRef = doc(db, 'users', user.uid);
                        const userDoc = await getDoc(userRef);
                        
                        if (userDoc.exists()) {
                            // Document exists - just update relevant fields
                            // This is the main path, as Cloud Function should create the initial document
                            const updateData = {
                                lastLogin: new Date(),
                                displayName: user.displayName || '',
                                photoURL: photoURL || ''
                            };
                            
                            await updateDoc(userRef, updateData);
                            console.log('User document updated with recent login info:', updateData);
                        } else {
                            // Fallback: Document doesn't exist - create it (in case Cloud Function failed)
                            const userData = {
                                uid: user.uid,
                                email: user.email,
                                displayName: user.displayName || '',
                                lastLogin: new Date(),
                                photoURL: photoURL || '',
                                createdAt: new Date(),
                                isActive: true
                            };
                            
                            await setDoc(userRef, userData);
                            console.log('User document created as fallback (Cloud Function may have failed)');
                        }
                    } catch (error) {
                        console.error('Error updating user document:', error);
                    }
                };
                
                updateUserDocument();
                
                // Check if the user is an admin
                const checkAdmin = async () => {
                    try {
                        // Force token refresh to ensure custom claims are updated
                        await user.getIdToken(true);
                        
                        // Check admin status
                        const isUserAdmin = await checkIsCurrentUserAdmin();
                        setIsAdmin(isUserAdmin);
                        console.log(`User admin status: ${isUserAdmin ? 'Admin' : 'Not admin'}`);
                    } catch (error) {
                        console.error('Error checking admin status:', error);
                        setIsAdmin(false);
                    } finally {
                        setAdminChecked(true);
                    }
                };

                checkAdmin();

                // Check if there's a redirect path saved
                const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
                if (savedRedirect) {
                    setRedirectPath(savedRedirect);
                    sessionStorage.removeItem('redirectAfterLogin');
                }
            } else {
                setUser(null);
                setIsAdmin(false);
                setAdminChecked(true);
            }
        });

        return () => unsubscribe();
    }, []);

    // Force admin status refresh after login
    useEffect(() => {
        if (isLoggedIn && !adminChecked) {
            const checkAdmin = async () => {
                try {
                    const isUserAdmin = await refreshAdminStatus();
                    setIsAdmin(isUserAdmin);
                    console.log(`Admin status refreshed: ${isUserAdmin ? 'Admin' : 'Not admin'}`);
                } catch (error) {
                    console.error('Error refreshing admin status:', error);
                } finally {
                    setAdminChecked(true);
                }
            };

            checkAdmin();
        }
    }, [isLoggedIn, adminChecked]);

    const handleGoogleLogin = async () => {
        try {
            setError('');
            await loginWithGoogle();
        } catch (error) {
            console.error('Google login error:', error);
            setError('Login error: ' + error.message);
        }
    };

    const handleLogout = async () => {
        try {
            setError('');
            await logoutUser();
        } catch (error) {
            console.error('Logout error:', error);
            setError('Logout error: ' + error.message);
        }
    };

    return (
        <div className="App">
            <Router>
                <Navbar isLoggedIn={isLoggedIn} onLogout={handleLogout} user={user} isAdmin={isAdmin} />
                <div className="main-content">
                    {redirectPath && <Navigate to={redirectPath} replace />}
                    {/* Only render routes when system is initialized */}
                    {systemInitialized ? (
                        <Routes>
                            <Route
                                path="/login"
                                element={
                                    isLoggedIn ?
                                        <Navigate to="/" /> :
                                        <LoginPage onLogin={handleGoogleLogin} />
                                }
                            />
                            <Route path="/pricing" element={<Pricing />} />
                            <Route path="/careers" element={<Career />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/privacy" element={<PrivacyPolicy />} />
                            <Route path="/terms" element={<TermsPage />} />
                            <Route path="/cookies" element={<CookiesPage />} />
                            <Route path="/documentation" element={<DocumentationPage />} />
                            <Route path="/support" element={<SupportPage />} />
                            <Route
                                path="/"
                                element={
                                    <div className="app-content">
                                        {error && <p className="error-message">{error}</p>}
                                        <HomePage />
                                    </div>
                                }
                            />
                            <Route
                                path="/dashboard"
                                element={
                                    isLoggedIn ?
                                        <Dashboard /> :
                                        <Navigate to="/login" state={{ from: "/dashboard" }} />
                                }
                            />
                            <Route
                                path="/new-project"
                                element={
                                    isLoggedIn ?
                                        <NewProject /> :
                                        <Navigate to="/login" state={{ from: "/new-project" }} />
                                }
                            />
                            <Route
                                path="/settings"
                                element={
                                    isLoggedIn ?
                                        <div className="app-content">
                                            <SystemSettings />
                                        </div> :
                                        <Navigate to="/login" state={{ from: "/settings" }} />
                                }
                            />
                            <Route
                                path="/account-settings"
                                element={
                                    isLoggedIn ?
                                        <div className="app-content">
                                            <AccountSettings />
                                        </div> :
                                        <Navigate to="/login" state={{ from: "/account-settings" }} />
                                }
                            />
                            <Route
                                path="/app-analysis/:fileId"
                                element={
                                    isLoggedIn ?
                                        <div className="app-content">
                                            <AppAnalysisDetails />
                                        </div> :
                                        <Navigate to="/login" state={{ from: "/app-analysis" }} />
                                }
                            />
                            <Route
                                path="/app-details/:fileId"
                                element={
                                    isLoggedIn ?
                                        <div className="app-content">
                                            <AppDetailsPage />
                                        </div> :
                                        <Navigate to="/login" state={{ from: "/app-details" }} />
                                }
                            />
                            <Route
                                path="/app-status/:fileId"
                                element={
                                    isLoggedIn ?
                                        <div className="app-content">
                                            <AppStatusPage />
                                        </div> :
                                        <Navigate to="/login" state={{ from: "/app-status" }} />
                                }
                            />
                            <Route
                                path="/admin/system-settings"
                                element={
                                    isLoggedIn ? (
                                        isAdmin ? (
                                            <div className="app-content">
                                                <SystemSettings />
                                            </div>
                                        ) : (
                                            <div className="app-content">
                                                <h1>Access Denied</h1>
                                                <p>You do not have permission to access this page.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Navigate to="/login" state={{ from: "/admin/system-settings" }} />
                                    )
                                }
                            />
                            <Route
                                path="/system-settings/user/:userId"
                                element={
                                    isLoggedIn ? (
                                        isAdmin ? (
                                            <div className="app-content">
                                                <AdminUserDetailsPage />
                                            </div>
                                        ) : (
                                            <div className="app-content">
                                                <h1>Access Denied</h1>
                                                <p>You do not have permission to access this page.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Navigate to="/login" state={{ from: "/system-settings/user" }} />
                                    )
                                }
                            />
                            <Route
                                path="/admin/manage-admins"
                                element={
                                    isLoggedIn ? (
                                        isAdmin ? (
                                            <div className="app-content">
                                                <AdminManagementPage />
                                            </div>
                                        ) : (
                                            <div className="app-content">
                                                <h1>Access Denied</h1>
                                                <p>You do not have permission to access this page.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Navigate to="/login" state={{ from: "/admin/manage-admins" }} />
                                    )
                                }
                            />
                            <Route
                                path="/admin/users"
                                element={
                                    isLoggedIn ? (
                                        isAdmin ? (
                                            <div className="app-content">
                                                <SystemSettings />
                                            </div>
                                        ) : (
                                            <div className="app-content">
                                                <h1>Access Denied</h1>
                                                <p>You do not have permission to access this page.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Navigate to="/login" state={{ from: "/admin/users" }} />
                                    )
                                }
                            />
                            <Route
                                path="/admin/card-templates"
                                element={
                                    isLoggedIn ? (
                                        isAdmin ? (
                                            <div className="app-content">
                                                <SystemSettings />
                                            </div>
                                        ) : (
                                            <div className="app-content">
                                                <h1>Access Denied</h1>
                                                <p>You do not have permission to access this page.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Navigate to="/login" state={{ from: "/admin/card-templates" }} />
                                    )
                                }
                            />
                            <Route
                                path="/admin"
                                element={
                                    isLoggedIn && isAdmin ?
                                        <div className="app-content">
                                            <AdminManagementPage />
                                        </div> :
                                        <Navigate to="/" />
                                }
                            />
                            <Route
                                path="/admin/deploy-status/:fileId"
                                element={
                                    isLoggedIn ? (
                                        isAdmin ? (
                                            <div className="app-content">
                                                <DeployStatusUpdatePage />
                                            </div>
                                        ) : (
                                            <div className="app-content">
                                                <h1>Access Denied</h1>
                                                <p>You do not have permission to access this page.</p>
                                            </div>
                                        )
                                    ) : (
                                        <Navigate to="/login" state={{ from: "/admin/deploy-status" }} />
                                    )
                                }
                            />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    ) : (
                        <Routes>
                            <Route path="*" element={
                                <div className="app-content">
                                    <HomePage />
                                </div>
                            } />
                        </Routes>
                    )}
                </div>
                <Footer />
                <CookieConsent />
            </Router>
        </div>
    );
}

export default App;
